import { FeatureFlagProvider as CandidateFeatureFlagProvider } from '@seek/candidate-feature-flag';
import { parse } from 'cookie';
import React from 'react';

import { convertStringToFixedUuid, useConfig } from '@seek/libs-shared';

import {
  LAUNCH_DARKLY_FEATURE_FLAGS_MAP,
  type LaunchDarklyFeatureFlagMap,
} from '../constants/launchDarklyFeatureFlags';
import { useAuth } from './authProvider';

type FeatureFlagContextProps<T extends Record<string, boolean>> =
  | {
      cookieFeatureFlags: T;
    }
  | undefined;

type FeatureFlagProviderProps = React.PropsWithChildren;

const FeatureFlagProviderContext =
  React.createContext<FeatureFlagContextProps<FeatureFlagsFromCookies>>(
    undefined,
  );

const parseCookies = () => {
  if (typeof document === 'undefined') {
    return {};
  }

  return parse(document.cookie);
};

const getFeatureFlagsFromCookies = (
  cookies: Record<string, string | undefined>,
) => {
  return {
    SINGLE_PAGE_REVIEW_FLOW: cookies.SINGLE_PAGE_REVIEW_FLOW === 'true',
    COMPANY_PROFILES_SALARIES_PAGE:
      cookies.COMPANY_PROFILES_SALARIES_PAGE === 'true',
    COMPANY_NAME_FIELD_V2: cookies.COMPANY_NAME_FIELD_V2 === 'true',
  } as const;
};
type FeatureFlagsFromCookies = ReturnType<typeof getFeatureFlagsFromCookies>;

const getLaunchDarklyFeatureFlags = () =>
  Object.keys(LAUNCH_DARKLY_FEATURE_FLAGS_MAP).map(
    (key) =>
      ({
        key: LAUNCH_DARKLY_FEATURE_FLAGS_MAP[
          key as keyof LaunchDarklyFeatureFlagMap
        ].key,
      }) satisfies Record<
        'key',
        LaunchDarklyFeatureFlagMap[keyof LaunchDarklyFeatureFlagMap]['key']
      >,
  );

export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  const { userId } = useAuth();
  const config = useConfig();
  const cookies = parseCookies();
  const cookieFeatureFlags = getFeatureFlagsFromCookies(cookies);

  return (
    <FeatureFlagProviderContext.Provider
      value={{
        cookieFeatureFlags: { ...cookieFeatureFlags },
      }}
    >
      {userId ? (
        <CandidateFeatureFlagProvider
          featureFlagsConfig={getLaunchDarklyFeatureFlags()}
          experimentsConfig={[]}
          solId={convertStringToFixedUuid(userId.toString())}
          debug={config.environment !== 'production'}
        >
          {children}
        </CandidateFeatureFlagProvider>
      ) : (
        children
      )}
    </FeatureFlagProviderContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = React.useContext(FeatureFlagProviderContext);

  if (context === undefined) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagProvider',
    );
  }

  return {
    ...context,
  };
};
